"use server";

import type { CountryDetailed, StateDetailed, Timezone } from "../types/index";
import db from "../utils/prisma";

export async function getAllTimezones(): Promise<Timezone[]> {
  return await db.timezone.findMany({
    select: {
      name: true,
      offset: true,
      offsetMinutes: true,
    },
    orderBy: { name: "asc" },
  });
}

export async function getTimezoneByName(
  name: string
): Promise<Timezone | null> {
  if (!name) return null;

  return await db.timezone.findUnique({
    where: { name },
    select: {
      name: true,
      offset: true,
      offsetMinutes: true,
      countryTimezones: {
        select: {
          country: {
            select: {
              code: true,
              name: true,
            },
          },
        },
      },
      stateTimezones: {
        select: {
          state: {
            select: {
              id: true,
              name: true,
            },
          },
        },
      },
    },
  });
}

export async function getTimezonesOfCountry(
  countryCode: string
): Promise<Timezone[]> {
  if (!countryCode) return [];

  const results = await db.country.findMany({
    where: { countryCode },
    select: {
      timezones: {
        select: {
          name: true,
          offset: true,
          offsetMinutes: true,
        },
      },
    },
  });

  return results
    .map((t: CountryDetailed) => t.timezones)
    .sort((a: Timezone, b: Timezone) => a.name.localeCompare(b.name));
}

export async function getTimezonesOfState(
  stateId: number
): Promise<Timezone[]> {
  if (!stateId) return [];

  const results = await db.state.findMany({
    where: { stateId },
    select: {
      timezones: {
        select: {
          name: true,
          offset: true,
          offsetMinutes: true,
        },
      },
    },
  });

  if (results.length > 0) {
    return results
      .map((t: StateDetailed) => t.timezones)
      .sort((a: Timezone, b: Timezone) => a.name.localeCompare(b.name));
  }

  const state = await db.state.findUnique({
    where: { id: parseInt(stateId.toString()) },
    select: { countryCode: true },
  });

  if (!state?.countryCode) return [];

  return await getTimezonesOfCountry(state.countryCode);
}

export async function getTimezoneOfCity(
  cityId: number
): Promise<Timezone | null> {
  if (!cityId) return null;

  const city = await db.city.findUnique({
    where: { id: parseInt(cityId.toString()) },
    select: {
      timezone: {
        select: {
          name: true,
          offset: true,
          offsetMinutes: true,
        },
      },
      stateId: true,
      countryCode: true,
    },
  });

  if (city?.timezone) return city.timezone;
  if (city?.stateId) {
    const stateTimezones = await getTimezonesOfState(city.stateId);
    return stateTimezones[0] || null;
  }
  if (city?.countryCode) {
    const countryTimezones = await getTimezonesOfCountry(city.countryCode);
    return countryTimezones[0] || null;
  }

  return null;
}
